/**
 * ProfilePage - Page de profil style Instagram
 * 
 * Profil Pro: Posts | Reels | Boutique | Salons | Événements | Galerie
 * Profil Particulier: Posts | Reels | Annonces | Salons | Événements | Galerie
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Settings, MoreHorizontal, Grid3X3, Play, ShoppingBag, 
  Users, Calendar, Images, MapPin, Link as LinkIcon, BadgeCheck,
  Heart, MessageCircle, Eye, Plus, Share2, Megaphone, X, ChevronLeft,
  ChevronRight, Volume2, VolumeX, Bookmark, PenTool
} from 'lucide-react';
import { TrustBadge, TrustBadgeIcon } from '../../../components/badges/TrustBadge';
import { FounderBadge, FounderBadgeIcon } from '../../../components/badges/FounderBadge';

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DÉMO
// ══════════════════════════════════════════════════════════════════════════════

const DEMO_PRO_PROFILE = {
  id: 'pro_martin',
  type: 'pro',
  name: 'Martin Plomberie',
  username: 'martin_plomberie',
  avatar: null,
  color: '#3B82F6',
  verified: true,
  metier: 'Plombier',
  city: 'Paris',
  bio: 'Plombier certifié depuis 15 ans. Spécialiste salle de bain et chauffage. Intervention rapide 7j/7.',
  website: 'www.martin-plomberie.fr',
  stats: {
    rating: 4.8,
    reviews: 47,
    annonces: 12,
    ventes: 89,
    followers: 2400,
    posts: 47,
    following: 156,
  },
  posts: [
    { id: 'p1', type: 'photo', image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&h=500&fit=crop', likes: 234 },
    { id: 'p2', type: 'reel', thumbnail: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=500&fit=crop', views: 12500 },
    { id: 'p3', type: 'photo', image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=500&fit=crop', likes: 189 },
    { id: 'p4', type: 'photo', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=500&fit=crop', likes: 312 },
    { id: 'p5', type: 'reel', thumbnail: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=500&fit=crop', views: 8700 },
    { id: 'p6', type: 'photo', image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=500&fit=crop', likes: 156 },
  ],
  reels: [
    { id: 'r1', thumbnail: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=500&fit=crop', views: 12500, duration: 45 },
    { id: 'r2', thumbnail: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=500&fit=crop', views: 8700, duration: 60 },
    { id: 'r3', thumbnail: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=500&fit=crop', views: 5400, duration: 90 },
  ],
  boutique: [
    { id: 'b1', name: 'Kit réparation fuite', price: '29,90 €', image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&h=400&fit=crop' },
    { id: 'b2', name: 'Robinet mitigeur pro', price: '89,00 €', image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=400&fit=crop' },
    { id: 'b3', name: 'Joint universel x10', price: '12,50 €', image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=400&fit=crop' },
    { id: 'b4', name: 'Flexible inox 80cm', price: '18,90 €', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop' },
  ],
  salons: [
    { id: 's1', name: 'Conseils plomberie', members: 234, image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=200&h=200&fit=crop' },
    { id: 's2', name: 'Astuces bricolage', members: 567, image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=200&h=200&fit=crop' },
  ],
  evenements: [
    { id: 'e1', name: 'Portes ouvertes atelier', date: '15 Avril 2025', attendees: 45 },
    { id: 'e2', name: 'Formation initiation plomberie', date: '22 Avril 2025', attendees: 12 },
  ],
  galerie: [
    { id: 'g1', image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=400&h=400&fit=crop' },
    { id: 'g2', image: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=400&fit=crop' },
    { id: 'g3', image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=400&fit=crop' },
    { id: 'g4', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop' },
    { id: 'g5', image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=400&fit=crop' },
    { id: 'g6', image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=400&fit=crop' },
  ],
};

const DEMO_PARTICULIER_PROFILE = {
  id: 'part_sophie',
  type: 'particulier',
  name: 'Sophie Durand',
  username: 'sophie_durand',
  avatar: null,
  color: '#EC4899',
  verified: false,
  city: 'Lyon',
  bio: 'Passionnée de déco et rénovation. Je partage mes projets maison !',
  stats: {
    rating: 4.5,
    reviews: 12,
    annonces: 5,
    ventes: 3,
    followers: 1200,
    posts: 23,
    following: 89,
  },
  posts: [
    { id: 'p1', type: 'photo', image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&h=500&fit=crop', likes: 156 },
    { id: 'p2', type: 'photo', image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=400&h=500&fit=crop', likes: 234 },
    { id: 'p3', type: 'reel', thumbnail: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&h=500&fit=crop', views: 4500 },
  ],
  reels: [
    { id: 'r1', thumbnail: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&h=500&fit=crop', views: 4500, duration: 30 },
  ],
  annonces: [
    { id: 'a1', title: 'Recherche carreleur pour SDB', budget: '2000-3000 €', status: 'active', date: 'Il y a 3j' },
    { id: 'a2', title: 'Peinture chambre enfant', budget: '500-800 €', status: 'active', date: 'Il y a 1 sem' },
    { id: 'a3', title: 'Installation climatisation', budget: '3000-4000 €', status: 'terminée', date: 'Il y a 2 sem' },
  ],
  salons: [
    { id: 's1', name: 'Déco maison', members: 1234, image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=200&h=200&fit=crop' },
  ],
  evenements: [],
  galerie: [
    { id: 'g1', image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400&h=400&fit=crop' },
    { id: 'g2', image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=400&h=400&fit=crop' },
    { id: 'g3', image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?w=400&h=400&fit=crop' },
  ],
};

// ══════════════════════════════════════════════════════════════════════════════
// VIEWER FULLSCREEN POUR REELS DU PROFIL
// ══════════════════════════════════════════════════════════════════════════════

function ProfileReelsViewer({ reels, startIndex, profile, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [direction, setDirection] = useState(0); // -1 = up (prev), 1 = down (next)
  const [isAnimating, setIsAnimating] = useState(false);
  const [doubleTapLike, setDoubleTapLike] = useState(false);
  const lastTap = useRef(0);
  
  const currentReel = reels[currentIndex];
  
  const goNext = () => {
    if (currentIndex < reels.length - 1 && !isAnimating) {
      setDirection(1);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setLiked(false);
        setIsAnimating(false);
      }, 150);
    }
  };
  
  const goPrev = () => {
    if (currentIndex > 0 && !isAnimating) {
      setDirection(-1);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setLiked(false);
        setIsAnimating(false);
      }, 150);
    }
  };
  
  // Swipe gesture
  const touchStartY = useRef(null);
  const touchStartTime = useRef(null);
  
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
  };
  
  const handleTouchEnd = (e) => {
    if (!touchStartY.current) return;
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    const velocity = Math.abs(diff) / (Date.now() - touchStartTime.current);
    
    // Swipe threshold: 50px or fast swipe
    if (diff > 50 || (diff > 20 && velocity > 0.3)) goNext();
    if (diff < -50 || (diff < -20 && velocity > 0.3)) goPrev();
    
    touchStartY.current = null;
  };
  
  // Double tap to like
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setLiked(true);
      setDoubleTapLike(true);
      setTimeout(() => setDoubleTapLike(false), 1000);
    }
    lastTap.current = now;
  };

  // Slide animation variants
  const slideVariants = {
    enter: (dir) => ({
      y: dir > 0 ? '100%' : '-100%',
      opacity: 0.5,
      scale: 0.95,
    }),
    center: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        y: { type: 'spring', stiffness: 400, damping: 35 },
        opacity: { duration: 0.2 },
        scale: { duration: 0.2 },
      },
    },
    exit: (dir) => ({
      y: dir > 0 ? '-100%' : '100%',
      opacity: 0.5,
      scale: 0.95,
      transition: {
        y: { type: 'spring', stiffness: 400, damping: 35 },
        opacity: { duration: 0.15 },
      },
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[10000] bg-black overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Reel avec animation */}
      <AnimatePresence initial={false} custom={direction} mode="popLayout">
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0"
          onClick={handleDoubleTap}
        >
          <img 
            src={currentReel.thumbnail} 
            alt="" 
            className="w-full h-full object-cover"
          />
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
          
          {/* Double tap heart animation */}
          <AnimatePresence>
            {doubleTapLike && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-50"
              >
                <Heart size={120} className="text-white fill-white drop-shadow-2xl" />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Play button central */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-20 h-20 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
              <Play size={40} className="text-white ml-1" fill="white" />
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
      
      {/* Header - fixed */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-20">
        <motion.button 
          whileTap={{ scale: 0.9 }}
          onClick={onClose} 
          className="p-2 rounded-full bg-black/20 backdrop-blur-sm"
        >
          <X size={26} className="text-white drop-shadow-lg" />
        </motion.button>
        <motion.span 
          key={currentIndex}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-white text-sm font-medium drop-shadow-lg bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full"
        >
          {currentIndex + 1} / {reels.length}
        </motion.span>
        <div className="w-10" />
      </div>
      
      {/* Badge DÉMO */}
      <div className="absolute top-16 left-4 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded z-20">
        DÉMO
      </div>
      
      {/* Actions à droite - fixed with stagger animation */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-5 z-20">
        {[
          { 
            icon: Heart, 
            active: liked, 
            onClick: () => setLiked(!liked),
            label: (currentReel.views / 100).toFixed(0),
            activeClass: 'text-red-500 fill-red-500'
          },
          { icon: MessageCircle, label: '12' },
          { 
            icon: Bookmark, 
            active: saved, 
            onClick: () => setSaved(!saved),
            activeClass: 'fill-white'
          },
          { icon: Share2 },
        ].map((action, i) => (
          <motion.button 
            key={i}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            whileTap={{ scale: 0.85 }}
            onClick={action.onClick}
            className="flex flex-col items-center gap-1"
          >
            <action.icon 
              size={28} 
              className={`text-white transition-all duration-200 ${action.active ? action.activeClass : ''}`} 
            />
            {action.label && <span className="text-white text-xs">{action.label}</span>}
          </motion.button>
        ))}
      </div>
      
      {/* Info en bas - animated */}
      <motion.div 
        key={`info-${currentIndex}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="absolute bottom-8 left-4 right-20 z-20"
      >
        <div className="flex items-center gap-3 mb-3">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold ring-2 ring-white/30"
            style={{ backgroundColor: profile.color }}
          >
            {profile.name[0]}
          </motion.div>
          <div>
            <div className="flex items-center gap-1">
              <span className="text-white font-semibold">{profile.name}</span>
              {profile.verified && <BadgeCheck size={16} className="text-blue-400 fill-blue-400" />}
            </div>
            <span className="text-white/70 text-sm">{profile.metier || profile.city}</span>
          </div>
        </div>
        <p className="text-white text-sm">Vidéo de démonstration • {currentReel.duration}s</p>
        <p className="text-white/60 text-xs mt-1">{currentReel.views?.toLocaleString()} vues</p>
      </motion.div>
      
      {/* Swipe indicators with animation */}
      <AnimatePresence>
        {currentIndex > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            className="absolute top-1/2 left-3 -translate-y-1/2 z-20"
          >
            <ChevronLeft size={28} className="text-white" />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {currentIndex < reels.length - 1 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            exit={{ opacity: 0 }}
            className="absolute top-1/2 right-3 -translate-y-1/2 z-20"
          >
            <ChevronRight size={28} className="text-white" />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Progress dots at bottom */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
        {reels.map((_, i) => (
          <motion.div 
            key={i}
            animate={{ 
              width: i === currentIndex ? 16 : 6,
              backgroundColor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)'
            }}
            transition={{ duration: 0.2 }}
            className="h-1.5 rounded-full"
          />
        ))}
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// VIEWER FULLSCREEN POUR PHOTOS DU PROFIL
// ══════════════════════════════════════════════════════════════════════════════

function ProfilePhotosViewer({ photos, startIndex, profile, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [doubleTapLike, setDoubleTapLike] = useState(false);
  const lastTap = useRef(0);
  
  const currentPhoto = photos[currentIndex];
  
  const goNext = () => {
    if (currentIndex < photos.length - 1 && !isAnimating) {
      setDirection(1);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setLiked(false);
        setIsAnimating(false);
      }, 100);
    }
  };
  
  const goPrev = () => {
    if (currentIndex > 0 && !isAnimating) {
      setDirection(-1);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex(currentIndex - 1);
        setLiked(false);
        setIsAnimating(false);
      }, 100);
    }
  };
  
  // Swipe gesture for horizontal navigation
  const touchStartX = useRef(null);
  
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 60) goNext();
    if (diff < -60) goPrev();
    touchStartX.current = null;
  };
  
  // Double tap to like
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setLiked(true);
      setDoubleTapLike(true);
      setTimeout(() => setDoubleTapLike(false), 1000);
    }
    lastTap.current = now;
  };

  // Horizontal slide variants
  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.92,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        x: { type: 'spring', stiffness: 350, damping: 30 },
        opacity: { duration: 0.2 },
        scale: { duration: 0.25, ease: 'easeOut' },
      },
    },
    exit: (dir) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0,
      scale: 0.92,
      transition: {
        x: { type: 'spring', stiffness: 350, damping: 30 },
        opacity: { duration: 0.15 },
      },
    }),
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[10000] bg-black flex flex-col"
    >
      {/* Header with animation */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="p-4 flex items-center justify-between"
      >
        <motion.button 
          whileTap={{ scale: 0.9 }}
          onClick={onClose} 
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <X size={26} className="text-white" />
        </motion.button>
        <div className="flex items-center gap-2">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ring-2 ring-white/20"
            style={{ backgroundColor: profile.color }}
          >
            {profile.name[0]}
          </motion.div>
          <span className="text-white font-medium">{profile.name}</span>
          {profile.verified && <BadgeCheck size={16} className="text-blue-400 fill-blue-400" />}
        </div>
        <div className="w-10" />
      </motion.div>
      
      {/* Photo container with swipe and animations */}
      <div 
        className="flex-1 relative flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            className="absolute inset-0 flex items-center justify-center"
            onClick={handleDoubleTap}
          >
            <motion.img 
              src={currentPhoto.image || currentPhoto.thumbnail} 
              alt="" 
              className="max-w-full max-h-full object-contain"
              layoutId={`photo-${currentPhoto.id}`}
            />
          </motion.div>
        </AnimatePresence>
        
        {/* Double tap heart animation */}
        <AnimatePresence>
          {doubleTapLike && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.3, opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none z-50"
            >
              <Heart size={100} className="text-white fill-white drop-shadow-2xl" />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Badge DÉMO */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded"
        >
          DÉMO
        </motion.div>
        
        {/* Navigation buttons with hover effects */}
        <AnimatePresence>
          {currentIndex > 0 && (
            <motion.button 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(0,0,0,0.7)' }}
              whileTap={{ scale: 0.95 }}
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={24} className="text-white" />
            </motion.button>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {currentIndex < photos.length - 1 && (
            <motion.button 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              whileHover={{ scale: 1.1, backgroundColor: 'rgba(0,0,0,0.7)' }}
              whileTap={{ scale: 0.95 }}
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-colors"
            >
              <ChevronRight size={24} className="text-white" />
            </motion.button>
          )}
        </AnimatePresence>
        
        {/* Animated dots indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
          {photos.map((_, i) => (
            <motion.div 
              key={i}
              animate={{ 
                width: i === currentIndex ? 20 : 8,
                backgroundColor: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="h-2 rounded-full cursor-pointer"
              onClick={() => {
                setDirection(i > currentIndex ? 1 : -1);
                setCurrentIndex(i);
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Actions footer with animation */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="p-4 border-t border-white/10"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-5">
            <motion.button 
              whileTap={{ scale: 0.85 }}
              onClick={() => setLiked(!liked)}
            >
              <motion.div
                animate={liked ? { scale: [1, 1.3, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <Heart 
                  size={28} 
                  className={`transition-all duration-200 ${liked ? 'text-red-500 fill-red-500' : 'text-white'}`} 
                />
              </motion.div>
            </motion.button>
            <motion.button whileTap={{ scale: 0.85 }}>
              <MessageCircle size={28} className="text-white" />
            </motion.button>
            <motion.button whileTap={{ scale: 0.85 }}>
              <Share2 size={26} className="text-white" />
            </motion.button>
          </div>
          <motion.button 
            whileTap={{ scale: 0.85 }}
            onClick={() => setSaved(!saved)}
          >
            <Bookmark size={28} className={`transition-all duration-200 ${saved ? 'text-white fill-white' : 'text-white'}`} />
          </motion.button>
        </div>
        <motion.p 
          key={`likes-${currentIndex}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-white font-semibold"
        >
          {currentPhoto.likes?.toLocaleString() || 0} j'aime
        </motion.p>
        <p className="text-white/60 text-sm mt-1">{currentIndex + 1} sur {photos.length}</p>
      </motion.div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  
  // Viewers fullscreen
  const [showReelsViewer, setShowReelsViewer] = useState(false);
  const [showPhotosViewer, setShowPhotosViewer] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Charger le profil depuis l'API ou utiliser les données démo
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      
      // Vérifier si c'est un profil démo
      if (username === 'martin_plomberie' || username === 'pro') {
        setProfile(DEMO_PRO_PROFILE);
        setLoading(false);
        return;
      }
      
      if (username === 'sophie_durand' || username === 'particulier') {
        setProfile(DEMO_PARTICULIER_PROFILE);
        setLoading(false);
        return;
      }
      
      // Essayer de charger le vrai profil depuis l'API
      try {
        const response = await fetch(`${API_URL}/api/ecosystem/social/profile/${username}`);
        
        if (response.ok) {
          const data = await response.json();
          // Convertir les données API au format attendu
          setProfile({
            id: data.id,
            type: data.user_type === 'professionnel' ? 'pro' : 'particulier',
            name: data.display_name || data.username,
            username: data.username,
            avatar: data.profile_photo,
            color: '#3B82F6',
            verified: data.is_verified || false,
            metier: data.metier,
            city: data.city,
            bio: data.bio || '',
            website: null,
            stats: {
              rating: data.rating || 0,
              reviews: data.reviews_count || 0,
              annonces: data.posts_count || 0,
              ventes: data.ventes_count || 0,
              followers: data.followers_count || 0,
              posts: data.posts_count || 0,
              following: data.following_count || 0,
            },
            posts: [],
            reels: [],
            boutique: [],
            salons: [],
            evenements: [],
            galerie: [],
            cover_photo: data.cover_photo,
            display_name: data.display_name,
          });
        } else {
          // Fallback vers profil démo si non trouvé
          setProfile(DEMO_PRO_PROFILE);
        }
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        setProfile(DEMO_PRO_PROFILE);
      } finally {
        setLoading(false);
      }
    };
    
    loadProfile();
  }, [username, API_URL]);
  
  // Afficher un loader pendant le chargement
  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  const isPro = profile.type === 'pro';
  const isDemo = username === 'martin_plomberie' || username === 'sophie_durand' || username === 'pro' || username === 'particulier';
  
  // Extraire les photos et reels séparément
  const photoPosts = profile.posts.filter(p => p.type === 'photo');
  const reelPosts = profile.reels || [];
  
  // Ouvrir viewer reels
  const openReelViewer = (index) => {
    setViewerStartIndex(index);
    setShowReelsViewer(true);
  };
  
  // Ouvrir viewer photos
  const openPhotoViewer = (index) => {
    setViewerStartIndex(index);
    setShowPhotosViewer(true);
  };
  
  // Onglets selon le type de profil
  const tabs = isPro 
    ? [
        { id: 'posts', icon: Grid3X3, label: 'Posts' },
        { id: 'reels', icon: Play, label: 'Reels' },
        { id: 'boutique', icon: ShoppingBag, label: 'Boutique' },
        { id: 'blog', icon: PenTool, label: 'Blog' },
        { id: 'salons', icon: Users, label: 'Salons' },
        { id: 'evenements', icon: Calendar, label: 'Événements' },
        { id: 'galerie', icon: Images, label: 'Galerie' },
      ]
    : [
        { id: 'posts', icon: Grid3X3, label: 'Posts' },
        { id: 'reels', icon: Play, label: 'Reels' },
        { id: 'annonces', icon: Megaphone, label: 'Annonces' },
        { id: 'blog', icon: PenTool, label: 'Blog' },
        { id: 'salons', icon: Users, label: 'Salons' },
        { id: 'evenements', icon: Calendar, label: 'Événements' },
        { id: 'galerie', icon: Images, label: 'Galerie' },
      ];

  // Contenu selon l'onglet actif
  const renderTabContent = () => {
    switch (activeTab) {
      case 'posts':
        return (
          <div className="grid grid-cols-3 gap-0.5">
            {profile.posts.map((post, idx) => {
              const photoIndex = photoPosts.findIndex(p => p.id === post.id);
              
              return (
                <div 
                  key={post.id} 
                  className="relative aspect-square bg-gray-100 cursor-pointer"
                  onClick={() => {
                    if (post.type === 'reel') {
                      const rIdx = profile.reels.findIndex(r => r.thumbnail === post.thumbnail);
                      openReelViewer(rIdx >= 0 ? rIdx : 0);
                    } else {
                      openPhotoViewer(photoIndex >= 0 ? photoIndex : 0);
                    }
                  }}
                >
                  <img 
                    src={post.type === 'photo' ? post.image : post.thumbnail} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                  {post.type === 'reel' && (
                    <div className="absolute top-2 right-2">
                      <Play size={16} className="text-white drop-shadow" fill="white" />
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white text-xs drop-shadow">
                    {post.type === 'photo' ? (
                      <>
                        <Heart size={12} fill="white" />
                        <span>{post.likes}</span>
                      </>
                    ) : (
                      <>
                        <Play size={12} fill="white" />
                        <span>{(post.views / 1000).toFixed(1)}K</span>
                      </>
                    )}
                  </div>
                  {/* Badge DÉMO */}
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                    DÉMO
                  </div>
                </div>
              );
            })}
          </div>
        );
        
      case 'reels':
        return (
          <div className="grid grid-cols-3 gap-0.5">
            {profile.reels.map((reel, idx) => (
              <div 
                key={reel.id} 
                className="relative aspect-[9/16] bg-gray-100 cursor-pointer"
                onClick={() => openReelViewer(idx)}
              >
                <img src={reel.thumbnail} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center">
                    <Play size={20} className="text-white ml-0.5" fill="white" />
                  </div>
                </div>
                <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white text-xs">
                  <Eye size={12} />
                  <span>{(reel.views / 1000).toFixed(1)}K</span>
                </div>
                <div className="absolute top-2 right-2 text-white text-[10px] bg-black/50 px-1 rounded">
                  {Math.floor(reel.duration / 60)}:{(reel.duration % 60).toString().padStart(2, '0')}
                </div>
                {/* Badge DÉMO */}
                <div className="absolute top-2 left-2 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                  DÉMO
                </div>
              </div>
            ))}
          </div>
        );
        
      case 'boutique':
        return (
          <div className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {profile.boutique?.map((product) => (
                <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="relative aspect-square bg-gray-100">
                    <img src={product.image} alt="" className="w-full h-full object-cover" />
                    {/* Badge DÉMO */}
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                      DÉMO
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                    <p className="text-sm font-bold text-[#F97316]">{product.price}</p>
                  </div>
                </div>
              ))}
            </div>
            {(!profile.boutique || profile.boutique.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <ShoppingBag size={48} className="mx-auto mb-3 opacity-30" />
                <p>Aucun produit dans la boutique</p>
              </div>
            )}
          </div>
        );
        
      case 'annonces':
        return (
          <div className="p-4 space-y-3">
            {profile.annonces?.map((annonce) => (
              <div key={annonce.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        annonce.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {annonce.status === 'active' ? 'ACTIVE' : 'TERMINÉE'}
                      </span>
                      <span className="text-[10px] text-gray-400">{annonce.date}</span>
                    </div>
                    <p className="font-medium text-gray-900">{annonce.title}</p>
                    <p className="text-sm text-[#F97316] font-semibold mt-1">{annonce.budget}</p>
                  </div>
                  {/* Badge DÉMO */}
                  <div className="bg-gray-100 text-gray-500 text-[8px] font-bold px-1.5 py-0.5 rounded">
                    DÉMO
                  </div>
                </div>
              </div>
            ))}
            {(!profile.annonces || profile.annonces.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <Megaphone size={48} className="mx-auto mb-3 opacity-30" />
                <p>Aucune annonce</p>
              </div>
            )}
          </div>
        );
        
      case 'salons':
        return (
          <div className="p-4 space-y-3">
            {profile.salons?.map((salon) => (
              <div key={salon.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  <img src={salon.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{salon.name}</p>
                  <p className="text-sm text-gray-500">{salon.members} membres</p>
                </div>
                <div className="bg-gray-100 text-gray-500 text-[8px] font-bold px-1.5 py-0.5 rounded">
                  DÉMO
                </div>
              </div>
            ))}
            {(!profile.salons || profile.salons.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <Users size={48} className="mx-auto mb-3 opacity-30" />
                <p>Aucun salon</p>
              </div>
            )}
          </div>
        );
        
      case 'evenements':
        return (
          <div className="p-4 space-y-3">
            {profile.evenements?.map((event) => (
              <div key={event.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{event.name}</p>
                    <p className="text-sm text-[#F97316]">{event.date}</p>
                    <p className="text-xs text-gray-500 mt-1">{event.attendees} participants</p>
                  </div>
                  <div className="bg-gray-100 text-gray-500 text-[8px] font-bold px-1.5 py-0.5 rounded">
                    DÉMO
                  </div>
                </div>
              </div>
            ))}
            {(!profile.evenements || profile.evenements.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <Calendar size={48} className="mx-auto mb-3 opacity-30" />
                <p>Aucun événement</p>
              </div>
            )}
          </div>
        );
        
      case 'galerie':
        return (
          <div className="grid grid-cols-3 gap-0.5">
            {profile.galerie?.map((item) => (
              <div key={item.id} className="relative aspect-square bg-gray-100">
                <img src={item.image} alt="" className="w-full h-full object-cover" />
                {/* Badge DÉMO */}
                <div className="absolute top-2 left-2 bg-black/60 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                  DÉMO
                </div>
              </div>
            ))}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="profile-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1">
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <div className="flex items-center gap-1">
            <span className="font-bold text-gray-900">{profile.username}</span>
            {profile.verified && <BadgeCheck size={18} className="text-blue-500 fill-blue-500" />}
            {profile.is_founder && <FounderBadgeIcon founderNumber={profile.founder_number} size={18} />}
            {profile.trust_level && <TrustBadgeIcon trustLevel={profile.trust_level} size={18} />}
          </div>
          <button className="p-1">
            <MoreHorizontal size={24} className="text-gray-900" />
          </button>
        </div>
      </div>
      
      {/* Cover Photo */}
      {profile.cover_photo && (
        <div 
          className="w-full h-32 bg-cover bg-center"
          style={{ backgroundImage: `url(${profile.cover_photo})` }}
        />
      )}
      
      {/* Profil Info */}
      <div className={`px-4 py-4 ${profile.cover_photo ? '-mt-10' : ''}`}>
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {profile.avatar || profile.profile_photo ? (
            <div 
              className={`w-20 h-20 rounded-full bg-cover bg-center flex-shrink-0 ${profile.cover_photo ? 'border-4 border-white' : ''}`}
              style={{ backgroundImage: `url(${profile.avatar || profile.profile_photo})` }}
            />
          ) : (
            <div 
              className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 ${profile.cover_photo ? 'border-4 border-white' : ''}`}
              style={{ backgroundColor: profile.color }}
            >
              {(profile.name || 'U')[0]}
            </div>
          )}
          
          {/* Stats */}
          <div className="flex-1">
            <div className="flex items-center justify-around text-center">
              <div>
                <div className="flex items-center justify-center gap-1">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                  <span className="font-bold text-gray-900">{profile.stats?.rating || 0}</span>
                </div>
                <p className="text-xs text-gray-500">{profile.stats?.reviews || 0} avis</p>
              </div>
              <div>
                <p className="font-bold text-gray-900">{profile.stats?.annonces || profile.stats?.posts || 0}</p>
                <p className="text-xs text-gray-500">Annonces</p>
              </div>
              <div>
                <p className="font-bold text-gray-900">{profile.stats?.ventes || 0}</p>
                <p className="text-xs text-gray-500">Ventes</p>
              </div>
              <div>
                <p className="font-bold text-gray-900">{profile.stats?.followers || 0}</p>
                <p className="text-xs text-gray-500">Abonnés</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Nom et bio */}
        <div className="mt-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-bold text-gray-900">{profile.display_name || profile.name}</h1>
            {isPro && (
              <span className="text-xs bg-[#1E3A5F] text-white px-2 py-0.5 rounded-full">
                PRO
              </span>
            )}
            {/* Badge Fondateur */}
            {profile.is_founder && <FounderBadge founderNumber={profile.founder_number} size="sm" showNumber={true} />}
            {/* Badge Cercle de Confiance */}
            {profile.trust_level && <TrustBadge trustLevel={profile.trust_level} size="sm" />}
            {/* Badge DÉMO - uniquement pour les profils de démo */}
            {isDemo && (
              <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-bold">
                DÉMO
              </span>
            )}
          </div>
          {isPro && (
            <p className="text-sm text-gray-500">{profile.metier}</p>
          )}
          <p className="text-sm text-gray-700 mt-1">{profile.bio}</p>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {profile.city}
            </span>
            {profile.website && (
              <a href={`https://${profile.website}`} className="flex items-center gap-1 text-blue-600">
                <LinkIcon size={14} />
                {profile.website}
              </a>
            )}
          </div>
        </div>
        
        {/* Boutons action */}
        <div className="flex gap-2 mt-4">
          <button 
            onClick={() => setIsFollowing(!isFollowing)}
            className={`flex-1 py-2 rounded-lg font-semibold text-sm transition-colors ${
              isFollowing 
                ? 'bg-gray-100 text-gray-900' 
                : 'bg-[#F97316] text-white'
            }`}
          >
            {isFollowing ? 'Abonné' : 'S\'abonner'}
          </button>
          <button className="flex-1 py-2 rounded-lg font-semibold text-sm bg-gray-100 text-gray-900">
            Message
          </button>
          <button className="px-4 py-2 rounded-lg bg-gray-100">
            <Share2 size={18} className="text-gray-700" />
          </button>
        </div>
      </div>
      
      {/* Onglets */}
      <div className="sticky top-[57px] z-40 bg-white border-b border-gray-100">
        <div className="flex overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[80px] flex flex-col items-center gap-1 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#1E3A5F] text-[#1E3A5F]'
                  : 'border-transparent text-gray-400'
              }`}
            >
              <tab.icon size={20} />
              <span className="text-[10px] font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Contenu des onglets */}
      <div className="pb-20">
        {renderTabContent()}
      </div>
      
      {/* Viewers fullscreen */}
      <AnimatePresence>
        {showReelsViewer && (
          <ProfileReelsViewer 
            reels={profile.reels}
            startIndex={viewerStartIndex}
            profile={profile}
            onClose={() => setShowReelsViewer(false)}
          />
        )}
        {showPhotosViewer && (
          <ProfilePhotosViewer 
            photos={photoPosts}
            startIndex={viewerStartIndex}
            profile={profile}
            onClose={() => setShowPhotosViewer(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
